// domain/model/Coverage.js
import {
  POLICYHOLDER_RELATIONSHIP,
  POLICYHOLDER_RELATIONSHIP_VALUES,
  PRIMARY_COB_RANK,
} from '../constants/coverage';
import { createPolicyholder } from './Policyholder';
import { applyVerdict, createEligibilityVerification } from './EligibilityVerification';
import {
  createEffectivePeriod,
  endsBeforeStart,
  overlaps,
  unreadableBounds,
} from './EffectivePeriod';

/**
 * Behavior of the Coverage aggregate: construction, its own invariants, and the sibling rule.
 *
 * The shape and the `@domainObject` tag live in `Coverage.types` — one concept, so one place
 * declares what it is.
 *
 * @see ./Coverage.types
 */

/** Validation failures, as codes. The domain does not know about i18n; the UI maps these. */
export const COVERAGE_ERRORS = {
  FUNDING_SOURCE_REQUIRED: 'fundingSourceRequired',
  EFFECTIVE_START_REQUIRED: 'effectiveStartRequired',
  EFFECTIVE_END_BEFORE_START: 'effectiveEndBeforeStart',
  UNSUPPORTED_POLICYHOLDER: 'unsupportedPolicyholder',
  POLICYHOLDER_REQUIRED: 'policyholderRequired',
  POLICYHOLDER_RELATIONSHIP_REQUIRED: 'policyholderRelationshipRequired',
  POLICYHOLDER_PERSON_REQUIRED: 'policyholderPersonRequired',
  DUPLICATE_OVERLAPPING_FUNDING_SOURCE: 'duplicateOverlappingFundingSource',
  UNREADABLE_EFFECTIVE_DATE: 'unreadableEffectiveDate',
};

/**
 * Builds a Coverage, filling defaults. Accepts a partial so it serves both "new, blank" and
 * "hydrate from storage".
 *
 * Effective bounds are accepted FLAT (`effectiveStart` / `effectiveEnd`) and assembled into the
 * `period` value object. Flat in, nested out: the two flat names are what the document, the form
 * draft and the projector all speak, so this factory is the one place a caller can hand over loose
 * bounds and get back a period that cannot come apart.
 *
 * NORMALIZATION HERE IS LOSSY, and deliberately asymmetric with `validateCoverage`. This produces
 * a well-formed entity, so an effective bound it cannot read becomes an open bound — which for an
 * end date reads as "ongoing". `validateCoverage` judges the RAW input and reports that case, so
 * the order matters: validate what the user or the store handed you, not what came back from here.
 * The edit dialog does exactly that, holding raw field values in its draft.
 *
 * The gap this leaves is the read path: a stored bound that is somehow corrupt is normalized to
 * null on load and never reaches validation. Nothing can write one today — the form is the only
 * writer and it validates first — so this is a latent risk, not a live one, and closing it means
 * deciding what a corrupt-coverage UI should even do.
 *
 * @param {Partial<import('./Coverage.types').Coverage> &
 *   { effectiveStart?: *, effectiveEnd?: * }} [attrs]
 * @returns {import('./Coverage.types').Coverage}
 */
export function createCoverage(attrs = {}) {
  return {
    id: attrs.id ?? null,
    clientId: attrs.clientId ?? null,
    clinicId: attrs.clinicId ?? null,
    fundingSourceId: attrs.fundingSourceId ?? null,
    // Flat in, nested out, exactly as the period is. The pair has an invariant of its own, so it
    // arrives as a value object rather than as two fields that can disagree.
    policyholder: createPolicyholder({
      personId: attrs.policyholderId,
      relationship: attrs.policyholderRelationship,
    }),
    // A fact with a source and a moment, not a bare yes/no — see EligibilityVerification for why
    // that matters before automation lands. A freshly entered coverage has not been checked, which
    // costs it a number in the billing order but never its position in the list. Whether billing
    // is PAUSED on it is not stored here at all: that is a BillingIntent, a decision rather than a
    // fact about the payer relationship.
    // Two inputs, meaning two different things. A stored fact arrives whole and keeps its stamp;
    // a form sends a flat `eligibilityVerified` verdict, which is a new check only if it differs
    // from what was there before.
    eligibilityVerification: attrs.eligibilityVerified === undefined
      ? createEligibilityVerification(attrs.eligibilityVerification)
      : applyVerdict(
        createEligibilityVerification(attrs.eligibilityVerification),
        attrs.eligibilityVerified === true,
      ),
    memberId: attrs.memberId ?? null,
    groupNumber: attrs.groupNumber ?? null,
    period: createEffectivePeriod(attrs.effectiveStart, attrs.effectiveEnd),
    cobRank: Number.isInteger(attrs.cobRank) ? attrs.cobRank : PRIMARY_COB_RANK,
    notes: attrs.notes ?? null,
    isDeleted: attrs.isDeleted === true,
  };
} // createCoverage()

/**
 * Do two coverages point at the same payer over periods that touch? Two overlapping coverages
 * for one funding source would make the projector's "lowest active cobRank" pick arbitrary,
 * so they are rejected at entry rather than resolved by luck at projection time.
 *
 * Takes anything carrying a payer and a period, so validation can ask it about a form draft that
 * is not yet an entity. Whether two spans of dates touch is the period's own question — this only
 * adds the payer half.
 *
 * @param {{ fundingSourceId: string|null,
 *   period: import('./EffectivePeriod').EffectivePeriod }} coverage
 * @param {{ fundingSourceId: string|null,
 *   period: import('./EffectivePeriod').EffectivePeriod }} other
 * @returns {boolean}
 */
export function overlapsSamePayer(coverage, other) {
  if (coverage.fundingSourceId === null || coverage.fundingSourceId !== other.fundingSourceId) {
    return false;
  }

  return overlaps(coverage.period, other.period);
} // overlapsSamePayer()

const namesSomebody = (personId) => typeof personId === 'string' && personId.trim() !== '';

/**
 * Validates a coverage on its own terms, and against its siblings when supplied.
 *
 * Takes RAW ATTRIBUTES, not a built entity — the flat `effectiveStart` / `effectiveEnd` a form or
 * a caller actually holds. That is the whole point of the asymmetry with `createCoverage`: once
 * the factory has run, an unreadable bound and a deliberately open one are both null and no longer
 * tellable apart. Validate the input, then build.
 *
 * @param {{ fundingSourceId?: string|null, policyholderId?: string|null,
 *   policyholderRelationship?: string, effectiveStart?: *, effectiveEnd?: *,
 *   id?: string|null }} attrs - raw, pre-normalization
 * @param {import('./Coverage.types').Coverage[]} [siblings] - the client's other live coverages
 * @returns {string[]} COVERAGE_ERRORS codes; empty when valid
 */
export function validateCoverage(attrs, siblings = []) {
  const errors = [];

  if (attrs.fundingSourceId === null || attrs.fundingSourceId === undefined) {
    errors.push(COVERAGE_ERRORS.FUNDING_SOURCE_REQUIRED);
  }

  // WHO PAYS IS REQUIRED, AND IS NOT ASSUMED. An absent relationship used to mean self, which reads
  // as harmless and is not: in ABA the client is usually a child and the policyholder usually a
  // parent, so the assumption records the less likely answer for anybody who never looked at the
  // field, and nothing downstream can tell that apart from a deliberate one.
  //
  // NONE OF THIS IS READINESS. These say the coverage does not identify who pays. Readiness is about
  // a named person's details being incomplete, and must never block a save.
  const relationship = attrs.policyholderRelationship ?? null;
  const personId = attrs.policyholderId ?? null;

  if (relationship === null) {
    // Two ways to have not finished, and they need different things next: pick somebody, or say
    // what the somebody you picked is to the client.
    errors.push(personId === null
      ? COVERAGE_ERRORS.POLICYHOLDER_REQUIRED
      : COVERAGE_ERRORS.POLICYHOLDER_RELATIONSHIP_REQUIRED);
  } else if (!POLICYHOLDER_RELATIONSHIP_VALUES.includes(relationship)) {
    errors.push(COVERAGE_ERRORS.UNSUPPORTED_POLICYHOLDER);
  } else if (relationship !== POLICYHOLDER_RELATIONSHIP.SELF && !namesSomebody(personId)) {
    // The half of the pair invariant a factory cannot enforce. Reported rather than corrected:
    // turning it back into self would record the client as their own policyholder against what the
    // user said.
    //
    // An id that is not a usable string counts as naming nobody. An empty one passes every check
    // written against null and then reads as absent everywhere downstream, so the coverage claims a
    // policyholder while no read can ever produce one.
    errors.push(COVERAGE_ERRORS.POLICYHOLDER_PERSON_REQUIRED);
  }

  // Asked of the RAW bounds, before the period is built. `toDateStr` refuses anything that is not
  // a calendar-date string — a Firestore Timestamp, a Date, a number, a malformed string — and
  // normalization turns every one of them into an open bound. So an end date the entity cannot
  // read would not throw or blank the field; it would quietly turn a coverage that ends into one
  // that never does, and the projector would keep billing that payer forever. The realistic way
  // that happens is a date input that speaks another type: the design system's `HrTimeField` reads
  // and writes Timestamps, so a future `HrDateField` following its sibling would too.
  if (unreadableBounds(attrs.effectiveStart, attrs.effectiveEnd)) {
    errors.push(COVERAGE_ERRORS.UNREADABLE_EFFECTIVE_DATE);
  }

  const period = createEffectivePeriod(attrs.effectiveStart, attrs.effectiveEnd);

  // A period is happy to be open at both ends; a coverage is not. Which bounds are REQUIRED is the
  // holder's rule, so it stays here — only the end-before-start invariant belongs to the period.
  if (period.start === null) {
    errors.push(COVERAGE_ERRORS.EFFECTIVE_START_REQUIRED);
  }
  if (endsBeforeStart(period)) {
    errors.push(COVERAGE_ERRORS.EFFECTIVE_END_BEFORE_START);
  }

  const candidate = { fundingSourceId: attrs.fundingSourceId ?? null, period };
  const hasOverlappingTwin = siblings
    .filter((sibling) => sibling.isDeleted !== true && sibling.id !== attrs.id)
    .some((sibling) => overlapsSamePayer(candidate, sibling));

  if (hasOverlappingTwin) {
    errors.push(COVERAGE_ERRORS.DUPLICATE_OVERLAPPING_FUNDING_SOURCE);
  }

  return errors;
} // validateCoverage()
